import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BridgeMessage,
  BridgeInstance,
  BridgeHealth,
  BridgeSkill,
  BridgePerson,
  BridgePersonDetail,
  TeamsChat,
  ChatRouting,
  UserRouting,
  PlannerPlan,
  PlannerTask,
  PlannerBucket,
  PlannerTaskUpdate,
  AiMaturityHistory,
  AiMaturitySnapshot,
  RynAccount,
  normalizeMessage,
  normalizeInstance,
  normalizeRynAccount,
} from '../models/bridge.models';
import {
  ContextSnapshot,
  QuotaRow,
  QuotaSampleRow,
} from '../models/flock-telemetry.models';
import { normalizeContextSnapshot } from '../models/flock-telemetry.utils';
import {
  RoiEntry,
  RoiSummary,
  SkillRoi,
  SkillRoiPatch,
  normalizeRoiEntry,
  normalizeRoiSummary,
  normalizeSkillRoi,
} from '../models/roi.models';
import {
  TokenHostRow,
  TokenProjectRow,
  TokenSummary,
} from '../models/token-cost.models';
import { environment } from '@ups/xplat/core';

/**
 * Shared Ryn Bridge service for communicating with the bridge API.
 * Used by both web (Software Security Roost page) and mobile (Ryn's Roost app).
 *
 * The base URL is injected via the RYN_BRIDGE_BASE_URL injection token,
 * allowing each app to configure its own bridge endpoint.
 */
@Injectable({ providedIn: 'root' })
export class RynBridgeService {
  private baseUrl = environment.urls!.bridgeAPI;

  constructor(private readonly http: HttpClient) {}

  /**
   * Configure the bridge API base URL.
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  getMessages(options?: {
    limit?: number;
    since?: string;
    type?: string;
  }): Observable<BridgeMessage[]> {
    let params = new HttpParams();
    if (options?.limit) params = params.set('limit', options.limit.toString());
    if (options?.since) params = params.set('since', options.since);
    if (options?.type) params = params.set('type', options.type);

    return this.http
      .get<Record<string, unknown>>(`${this.baseUrl}/messages`, { params })
      .pipe(
        map((r) => {
          const msgs = (r['messages'] ?? r['Messages'] ?? []) as Record<
            string,
            unknown
          >[];
          return msgs.map(normalizeMessage);
        })
      );
  }

  getInstanceMessages(
    instanceId: string,
    options?: { markRead?: boolean; since?: string; type?: string }
  ): Observable<BridgeMessage[]> {
    let params = new HttpParams();
    if (options?.markRead !== undefined)
      params = params.set('mark_read', options.markRead.toString());
    if (options?.since) params = params.set('since', options.since);
    if (options?.type) params = params.set('type', options.type);

    return this.http
      .get<
        Record<string, unknown>
      >(`${this.baseUrl}/messages/${encodeURIComponent(instanceId)}`, { params })
      .pipe(
        map((r) => {
          const msgs = (r['messages'] ?? r['Messages'] ?? []) as Record<
            string,
            unknown
          >[];
          return msgs.map(normalizeMessage);
        })
      );
  }

  getInstances(): Observable<BridgeInstance[]> {
    return this.http
      .get<
        Record<string, unknown>[] | Record<string, unknown>
      >(`${this.baseUrl}/instances`)
      .pipe(
        map((r) => {
          const insts = Array.isArray(r)
            ? r
            : ((r['instances'] ?? r['Instances'] ?? []) as Record<
                string,
                unknown
              >[]);
          return insts.map(normalizeInstance);
        })
      );
  }

  getHealth(): Observable<BridgeHealth> {
    return this.http.get<BridgeHealth>(`${this.baseUrl}/health`);
  }

  /**
   * Authoritative account store (governance pane, ADO #30033 Phase 1b):
   * the Entra identities the flock holds and which seat each routes to.
   * Requires a bridge Bearer JWT server-side; metadata only, never secrets.
   */
  getRynAccounts(): Observable<RynAccount[]> {
    return this.http
      .get<Record<string, unknown>>(`${this.baseUrl}/accounts`)
      .pipe(
        map((r) => {
          const accounts = (r['accounts'] ?? r['Accounts'] ?? []) as Record<
            string,
            unknown
          >[];
          return accounts.map(normalizeRynAccount);
        })
      );
  }

  getSkills(): Observable<BridgeSkill[]> {
    return this.http.get<BridgeSkill[]>(`${this.baseUrl}/skills`);
  }

  sendMessage(message: {
    from: string;
    to: string;
    type: string;
    subject: string;
    body: string;
  }): Observable<{ sent: BridgeMessage }> {
    return this.http.post<{ sent: BridgeMessage }>(
      `${this.baseUrl}/messages`,
      message
    );
  }

  /**
   * "Update analysis" button on the Ryn Reports page (#30429): asks the
   * bridge to dispatch a regen request for the report's written analysis to
   * the authoring seat. 202 means queued, not regenerated; the rewritten
   * analysis lands as a blob upload minutes later.
   */
  requestReportAnalysisRefresh(
    slug: string
  ): Observable<{ queued: boolean; slug: string; target: string }> {
    return this.http.post<{ queued: boolean; slug: string; target: string }>(
      `${this.baseUrl}/reports/analysis-refresh`,
      { slug }
    );
  }

  cycleInstance(
    instanceId: string,
    account?: string
  ): Observable<{ sent: BridgeMessage }> {
    const accountFlag = account ? ` --account ${account}` : '';
    return this.sendMessage({
      from: 'ryns-roost',
      to: 'prime-ryn',
      type: 'direct',
      subject: `CYCLE REQUEST: ${instanceId}`,
      body: `Ryn's Roost requested cycle for ${instanceId}${account ? ` on ${account} account` : ''}. Run: bash /c/Projects/UPS1_Workspace/tools/scripts/ryn-cycle.sh ${instanceId}${accountFlag} &`,
    });
  }

  // --- Flock telemetry (read-only) ---

  /** Claude quota windows across all accounts (GET /ryn-db/quota). */
  getQuota(): Observable<QuotaRow[]> {
    return this.http
      .get<QuotaRow[]>(`${this.baseUrl}/ryn-db/quota`)
      .pipe(map((rows) => rows ?? []));
  }

  /** Weekly usage history (US 34691, GET /ryn-db/quota/samples), oldest
   * first per account, for the 7d projection's 12h slope. */
  getQuotaSamples(sinceHours: number): Observable<QuotaSampleRow[]> {
    const params = new HttpParams()
      .set('sinceHours', sinceHours.toString())
      .set('windowType', 'Weekly');
    return this.http
      .get<{
        samples?: QuotaSampleRow[];
      }>(`${this.baseUrl}/ryn-db/quota/samples`, { params })
      .pipe(map((resp) => resp?.samples ?? []));
  }

  getQuotaByUpn(upn: string): Observable<QuotaRow[]> {
    return this.http
      .get<
        QuotaRow[]
      >(`${this.baseUrl}/ryn-db/quota/by-upn/${encodeURIComponent(upn)}`)
      .pipe(map((rows) => rows ?? []));
  }

  /** Per-seat context snapshots (GET /ryn-db/context). */
  getContextSnapshots(staleMinutes?: number): Observable<ContextSnapshot[]> {
    let params = new HttpParams();
    if (staleMinutes) {
      params = params.set('staleMinutes', staleMinutes.toString());
    }
    return this.http
      .get<ContextSnapshot[]>(`${this.baseUrl}/ryn-db/context`, { params })
      .pipe(map((rows) => (rows ?? []).map(normalizeContextSnapshot)));
  }

  getContextSnapshot(instanceId: string): Observable<ContextSnapshot> {
    return this.http
      .get<ContextSnapshot>(
        `${this.baseUrl}/ryn-db/context/${encodeURIComponent(instanceId)}`
      )
      .pipe(map(normalizeContextSnapshot));
  }

  /**
   * ROI/EBITDA all-time authoritative rollup (GET /ryn-db/roi-entries/summary).
   * The response has NO summed dollar total by design (honesty firewall); dollars
   * come only per value class. Normalized so a null body yields a safe empty summary.
   */
  getRoiSummary(): Observable<RoiSummary> {
    return this.http
      .get<
        Record<string, unknown>
      >(`${this.baseUrl}/ryn-db/roi-entries/summary`)
      .pipe(map((raw) => normalizeRoiSummary(raw)));
  }

  /**
   * ROI entries feed (GET /ryn-db/roi-entries), one row per value claim, newest
   * first. The dashboard buckets these client-side for the window view + freshness
   * stamp (the summary carries no per-window / newest field). Null body -> empty list.
   */
  getRoiEntries(): Observable<RoiEntry[]> {
    return this.http
      .get<Record<string, unknown>[]>(`${this.baseUrl}/ryn-db/roi-entries`)
      .pipe(map((rows) => (rows ?? []).map(normalizeRoiEntry)));
  }

  /**
   * Skill ROI projection inputs (GET /ryn-db/skill-roi), one row per skill (WI 31106).
   * Null body -> empty list, so a backend predating the endpoint degrades to an
   * honest empty section instead of an error.
   */
  getSkillRoi(): Observable<SkillRoi[]> {
    return this.http
      .get<Record<string, unknown>[]>(`${this.baseUrl}/ryn-db/skill-roi`)
      .pipe(map((rows) => (rows ?? []).map(normalizeSkillRoi)));
  }

  /**
   * Sparse update of a skill's ROI inputs (PUT /ryn-db/skill-roi/{id}). Send only the
   * fields being edited; the endpoint never nulls an unsent column. Edit history is
   * kept server-side by the SkillRoi temporal table.
   */
  updateSkillRoi(
    skillRoiId: string,
    patch: SkillRoiPatch
  ): Observable<unknown> {
    return this.http.put(
      `${this.baseUrl}/ryn-db/skill-roi/${skillRoiId}`,
      patch
    );
  }

  // --- RynCore token costs (read-only, public allowlist endpoints) ---

  /** Fleet-wide token spend summary (GET /ryncore/tokens). */
  getTokenSummary(): Observable<TokenSummary> {
    return this.http.get<TokenSummary>(`${this.baseUrl}/ryncore/tokens`);
  }

  /** Per-host token spend rollup (GET /ryncore/tokens/hosts). */
  getTokenHosts(): Observable<TokenHostRow[]> {
    return this.http
      .get<TokenHostRow[]>(`${this.baseUrl}/ryncore/tokens/hosts`)
      .pipe(map((rows) => rows ?? []));
  }

  /** Per-project token spend rollup (GET /ryncore/tokens/projects). */
  getTokenProjects(): Observable<TokenProjectRow[]> {
    return this.http
      .get<TokenProjectRow[]>(`${this.baseUrl}/ryncore/tokens/projects`)
      .pipe(map((rows) => rows ?? []));
  }

  getPersons(includeInactive = false): Observable<BridgePerson[]> {
    const params = includeInactive ? '?active=false' : '';
    return this.http.get<BridgePerson[]>(
      `${this.baseUrl}/ryn-db/persons${params}`
    );
  }

  getPerson(id: number): Observable<BridgePersonDetail> {
    return this.http.get<BridgePersonDetail>(
      `${this.baseUrl}/ryn-db/persons/${id}`
    );
  }

  updatePerson(
    id: number,
    updates: Partial<BridgePerson>
  ): Observable<{ updated: number }> {
    return this.http.patch<{ updated: number }>(
      `${this.baseUrl}/ryn-db/persons/${id}`,
      updates
    );
  }

  rateAiMaturity(
    personId: number,
    rating: number,
    ratedBy?: string,
    notes?: string
  ): Observable<{ personId: number; rating: number; recorded: boolean }> {
    return this.http.post<{
      personId: number;
      rating: number;
      recorded: boolean;
    }>(`${this.baseUrl}/ryn-db/persons/${personId}/ai-maturity`, {
      rating,
      ratedBy,
      notes,
    });
  }

  getAiMaturityHistory(personId: number): Observable<AiMaturityHistory[]> {
    return this.http.get<AiMaturityHistory[]>(
      `${this.baseUrl}/ryn-db/persons/${personId}/ai-maturity`
    );
  }

  getAiMaturitySnapshot(date?: string): Observable<AiMaturitySnapshot[]> {
    const params = date ? `?date=${date}` : '';
    return this.http.get<AiMaturitySnapshot[]>(
      `${this.baseUrl}/ryn-db/ai-maturity/snapshot${params}`
    );
  }

  // --- Teams Chats ---

  getTeamsChats(limit = 50): Observable<TeamsChat[]> {
    return this.http
      .get<{ chats: TeamsChat[] }>(`${this.baseUrl}/teams-chats`, {
        params: new HttpParams().set('limit', limit.toString()),
      })
      .pipe(map((r) => r.chats));
  }

  // --- Chat Routing ---

  getChatRoutings(): Observable<ChatRouting[]> {
    return this.http.get<ChatRouting[]>(`${this.baseUrl}/routing/chats`);
  }

  getChatRouting(chatId: string): Observable<ChatRouting> {
    return this.http.get<ChatRouting>(
      `${this.baseUrl}/routing/chat/${encodeURIComponent(chatId)}`
    );
  }

  setChatRouting(
    chatId: string,
    payload: {
      defaultInstances: string;
      displayName?: string;
      members?: string;
      setBy?: string;
    }
  ): Observable<ChatRouting & { message: string }> {
    return this.http.put<ChatRouting & { message: string }>(
      `${this.baseUrl}/routing/chat/${encodeURIComponent(chatId)}`,
      payload
    );
  }

  // --- User Routing ---

  getUserRoutings(): Observable<UserRouting[]> {
    return this.http.get<UserRouting[]>(`${this.baseUrl}/routing/users`);
  }

  setUserRouting(
    userId: string,
    payload: { defaultInstance: string; displayName?: string; setBy?: string }
  ): Observable<UserRouting & { message: string }> {
    return this.http.put<UserRouting & { message: string }>(
      `${this.baseUrl}/routing/user/${encodeURIComponent(userId)}`,
      payload
    );
  }

  getPlans(): Observable<PlannerPlan[]> {
    return this.http
      .get<{ plans: PlannerPlan[] }>(`${this.baseUrl}/planner/plans`)
      .pipe(map((r) => r.plans ?? []));
  }

  getPlanTasks(
    planId: string,
    options?: { status?: string; instance?: string }
  ): Observable<PlannerTask[]> {
    let params = new HttpParams();
    if (options?.status) params = params.set('status', options.status);
    if (options?.instance) params = params.set('instance', options.instance);

    return this.http
      .get<{
        tasks: PlannerTask[];
      }>(`${this.baseUrl}/planner/plans/${encodeURIComponent(planId)}/tasks`, {
        params,
      })
      .pipe(map((r) => r.tasks ?? []));
  }

  getPlanBuckets(planId: string): Observable<PlannerBucket[]> {
    return this.http
      .get<{
        buckets: PlannerBucket[];
      }>(`${this.baseUrl}/planner/plans/${encodeURIComponent(planId)}/buckets`)
      .pipe(map((r) => r.buckets ?? []));
  }

  getTaskDetails(
    taskId: string
  ): Observable<{ taskId: string; description: string }> {
    return this.http.get<{ taskId: string; description: string }>(
      `${this.baseUrl}/planner/tasks/${encodeURIComponent(taskId)}/details`
    );
  }

  updateTask(
    taskId: string,
    updates: PlannerTaskUpdate
  ): Observable<{ updated: boolean; taskId: string }> {
    return this.http.patch<{ updated: boolean; taskId: string }>(
      `${this.baseUrl}/planner/tasks/${encodeURIComponent(taskId)}`,
      updates
    );
  }

  getTimeSince(lastSeen: string): string {
    const diff = Date.now() - new Date(lastSeen).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m ago`;
  }

  isActive(lastSeen: string): boolean {
    return Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
  }

  formatUptime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
}
